import * as T from 'three';
import { getJointHeirarchy } from '../robotFunctions/jointHeirarchy';
import { Plane, getBestPlane } from '../dataAnalysis/plane';
import { average, averageSmoothing, clusterPoints, findBestPoints, getDerivative, getExtremas, spaceData, standardDeviation } from '../dataAnalysis/transformData';
import { exportToCsv } from '../helpers';
import { animateRobot } from '../robotFunctions/animateRobot';
import { createSeperations, getForwardDirection, getRobotPositions, measureCollision } from '../dataAnalysis/positionData';
import { Vector3 } from 'three';
import { combineHeuristics, getPoseCollisionDirection, getMotionPlaneDirection, getAverageForwardDirection, extendViewDistance } from '../dataAnalysis/viewingHeuristics';

/**
 * This class is a per robot interface. It handles viewing and pose selection heuristics.
 * It utilizes many functions from the dataAnalysis folder which contains plain.js, positionData.js, transformData.js, and viewingHeuristics.js
 */

export class DataInterface {
    constructor(robotGroup) {
        this.robotGroup = robotGroup;
        this.interfaces = this.robotGroup.interfaces
        this.viewFromPoint = false;

        // physical representation of the average of joint axises
        this.averageAxis = new T.Group();
        let mesh1 = new T.Mesh(new T.CylinderGeometry(0.004, 0.004, 2, 10, 10), new T.MeshStandardMaterial({color: "yellow"}));
        let mesh2 = new T.Mesh(new T.SphereGeometry(0.02),  new T.MeshStandardMaterial({color: "white"}))
        let mesh3 = new T.Mesh(new T.SphereGeometry(0.02),  new T.MeshStandardMaterial({color: "purple"}))
        this.averageAxis.add(mesh1)
        this.averageAxis.add(mesh2)
        this.averageAxis.add(mesh3)
        mesh3.position.y = 1
        this.averageAxis.visible = false
        this.planes = []

        // joint normal axises
        this.jointAxis = []
        this.jointSpheres = []
        // go through all joints
        this.robotGroup.jointInterface.getJoints().forEach(joint => {
            // set up model for joint normal axis
            let group = new T.Group();
            let mesh1 = new T.Mesh( new T.CylinderGeometry(0.002, 0.002, 0.1, 4, 2), new T.MeshStandardMaterial({
                color: "pink"
            }));
            let mesh2 = new T.Mesh(new T.SphereGeometry(0.01),  new T.MeshStandardMaterial({color: "green"}))
            let mesh3 = new T.Mesh(new T.SphereGeometry(0.01),  new T.MeshStandardMaterial({color: "red"}))
            group.add(mesh1)
            group.add(mesh2)
            group.add(mesh3)
            mesh3.position.y = 0.05
            group.visible = false
            // add model
            this.interfaces.scene.add(group);
            let axis = {
                group: group,
                directionMesh: mesh3
            }
            this.jointAxis.push(axis)
            this.jointSpheres.push(mesh2)
        });
        this.jointsViewVector = new T.Vector3()
        this.jointsNormalVector = new T.Vector3()

        // joint plane
        this.jointPlane = new Plane(this.interfaces.scene, new T.Vector3(), new T.Vector3(0, 1, 0))

        // joint heirarchy
        this.jointHeirarchy = getJointHeirarchy(this.robotGroup.robot)

        // collision spheres
        this.spheres = []
        this.links = this.fitCollisionSpheres()

        // planes map
        this.planesMap = new Map()

        // camera points
        this.cameraPoints = []
        let colors = ["purple", "red", "yellow"]
        for(let i = 0; i < 9; i++) {
            let viewPoint = new T.Mesh(new T.SphereGeometry(0.02),  new T.MeshStandardMaterial({color: "white"}))
            this.interfaces.scene.add(viewPoint);
            viewPoint.visible = false
            this.cameraPoints.push(viewPoint)
        }

        // visual representaitons for the camera viewing point and camera look at point
        this.viewPoint = new T.Mesh(new T.SphereGeometry(0.02),  new T.MeshStandardMaterial({color: "white"}))
        this.viewPoint.visible = false
        this.interfaces.scene.add(this.viewPoint);
        this.focusPoint = new T.Mesh(new T.SphereGeometry(0.02),  new T.MeshStandardMaterial({color: "white"}))
        this.focusPoint.visible = false
        this.interfaces.scene.add(this.focusPoint);

        // camera heuristics
        this.cameraHeuristics = []
        this.cameraWeights = [1, 1, 1, 1, 1, 1]
        this.distanceFromFocus = 2.5

        // pose extraction settings
        this.numCuts = 3
        this.cutScoreThreshold = 100
        this.weightDecrement = 0.2
    }

    /**
     * Called on each frame to update the robot's data in real time
     */
    update() {
        this.updateAxis()
        this.updateCollisionSpheres()
        this.updateCamera()
    }

    /**
     * Updates the joints' rotational normal axises as well as update the average of them
     */
    updateAxis() {
        let directionalVectors = []
        let positionVectors = []
        let jointNames = []
        let coneIndex = 0;

        // go through all joints
        this.robotGroup.jointInterface.getJoints().forEach(joint => {
            let pos = joint[1].getWorldPosition(new T.Vector3);
            let rot = joint[1].getWorldQuaternion(new T.Quaternion);

            // update axis
            this.jointAxis[coneIndex].group.position.set(pos.x, pos.y, pos.z)
            this.jointAxis[coneIndex].group.quaternion.copy(rot);

            // certain axixes rotate differently (described in urdf), either x or y
            if(joint[1].axis.x == 1) {
                this.jointAxis[coneIndex].group.rotateOnAxis(new T.Vector3(0, 0, 1), Math.PI/2)
            } else if(joint[1].axis.z == 1) {
                this.jointAxis[coneIndex].group.rotateOnAxis(new T.Vector3(1, 0, 0), Math.PI/2)
            }

            // for average calculations later
            let dir = this.jointAxis[coneIndex].directionMesh.getWorldPosition(new T.Vector3());
            let direction = new T.Vector3(dir.x - pos.x, dir.y - pos.y, dir.z - pos.z)
            directionalVectors.push(direction)
            positionVectors.push(pos)
            jointNames.push(joint[1].name)
            coneIndex++;
        })

        // get average joint normal axis
        let averageDirection = new T.Vector3(0, 0, 0)
        let averagePosition = new T.Vector3(0, 0, 0)
        for(let i = 0; i < directionalVectors.length; i++) {
            let weight = Math.max(0, 1 - this.jointHeirarchy.get(jointNames[i]).depth * this.weightDecrement)
            averageDirection.add(directionalVectors[i].multiplyScalar(weight))
            averagePosition.add(positionVectors[i])
        }
        averageDirection.normalize()
        averageDirection.multiplyScalar(this.distanceFromFocus)
        averagePosition.divideScalar(directionalVectors.length)

        this.jointsViewVector.copy(averagePosition)
        this.jointsNormalVector.copy(averageDirection)

        this.averageAxis.position.set(averagePosition.x, averagePosition.y, averagePosition.z)
        this.averageAxis.lookAt(new T.Vector3(averagePosition.x + averageDirection.x, averagePosition.y + averageDirection.y, averagePosition.z + averageDirection.z))

        // because all joints posiitons are collected here, may as well calculate best fit plane for joints here and update
        if(positionVectors.length >= 3) {
            let planeData = getBestPlane(positionVectors, false)
            this.jointPlane.updatePlane(planeData.point, planeData.normal)
        }
    }

    /**
     * Creates a set of links that represent an approximation of the robot's collision model
     * It essentially fits spheres between each pairs of joints that make up a link
     * This is called one time in the constructor
     * @returns an array of links which contain a pair of joints, the spheres between them, and distance between joints
     */
    fitCollisionSpheres() {
        const sphereSize = 0.04
        // generate joint pairs
        let links = []
        let index = 0
        // go through all joints
        Array.from(this.jointHeirarchy.values()).forEach(value => {
            // checks if the joint has a parent joint(then this would joint would and the last would form a link)
            if(value.parent) {
                // fill in spheres between joints
                let spheres = []
                let dist = value.joint.getWorldPosition(new T.Vector3).distanceTo(value.parent.getWorldPosition(new T.Vector3))
                let numSpheres = Math.floor(dist/(sphereSize*2)) + 1
                for(let i = 0; i < numSpheres; i++) {
                    let mesh = new T.Mesh(new T.SphereGeometry(sphereSize),  new T.MeshStandardMaterial({color: "purple", transparent: true, opacity: 0.3}))
                    mesh.visible = false
                    this.interfaces.scene.add(mesh)
                    spheres.push(mesh)
                    if(index > 1) { // skip first two links for collision spheres
                        this.spheres.push(mesh)
                    }
                }
                let link = {
                    pair: [value.joint, value.parent],
                    spheres: spheres,
                    distance: dist
                }
                links.push(link)
                index++
            }
        })
        // console.log(links)
        return links
    }

    /**
     * Updates the collision spheres
     */
    updateCollisionSpheres() {
        this.links.forEach(link => {
            let pos1 = link.pair[0].getWorldPosition(new T.Vector3)
            let pos2 = link.pair[1].getWorldPosition(new T.Vector3)
            for(let i = 0; i < link.spheres.length; i++) {
                let pos = new T.Vector3()
                pos.lerpVectors(pos1, pos2, i/link.spheres.length)
                link.spheres[i].position.set(pos.x, pos.y, pos.z)
            }
        })
    }

    /**
     * Saves a best fit plane to a motion name
     * @param {String} motionName Name of a motion
     * @param {Object} plane The best fit plane of the motion
     */
    addMotionPlane(motionName, plane) {
        this.planesMap.set(motionName, plane)
    }

    // getNormals() {
    //     let pos1 = this.jointsViewVector.copy()
    //     let pos2 = this.jointPlane.point.copy()

    //     let norm1 = this.jointsNormalVector.copy()
    //     let norm2 = this.jointPlane.normal.copy()

    //     return [
    //         [pos1, norm1]
    //         [pos2, norm2]
    //     ]
    // }

    getCamera() {
        let viewPoint = this.viewPoint.getWorldPosition(new T.Vector3())
        let focusPoint = this.focusPoint.getWorldPosition(new T.Vector3())
        return [viewPoint, focusPoint]
    }

    getPoseHeuristics(motionData) {
        motionData.shift()
        motionData = spaceData(motionData, 0.1)
        for(let i = 0; i < 10; i++) {
            motionData = averageSmoothing(motionData, 1)
        }

        let velExtremas = getExtremas(motionData, 4, motionData[motionData.length-1][0]/20)

        let derivatives = getDerivative(motionData)
        for(let i = 0; i < 5; i++) {
            derivatives = averageSmoothing(derivatives, 1)
        }

        let jointRotExtremas = []
        let jointVelExtremas = []

        let avg1 = average(motionData)
        let avg2 = average(derivatives)

        let std1 = standardDeviation(motionData)
        let std2 = standardDeviation(derivatives)

        for(let i = 5; i < derivatives[0].length; i++) {
            let zeroes = getExtremas(motionData, i, motionData[motionData.length-1][0]/20)
            let extremas = getExtremas(derivatives, i, derivatives[derivatives.length-1][0]/20)

            zeroes = zeroes.filter(point => point[1] > avg1[i]+std1[i] || point[1] < avg1[i]-std1[i])
            extremas = extremas.filter(point => point[1] > avg2[i]+std2[i] || point[1] < avg2[i]-std2[i])

            jointRotExtremas.push(zeroes.map(x => x[0]))
            jointVelExtremas.push(extremas.map(x => x[0]))
        }

        let weights = []
        for(let i = 0; i < jointRotExtremas.length; i++) {
            let value = 1 - this.weightDecrement * i
            value = value >= 0 ? value : 0
            weights.push(value)
        }

        let optRotPoints = findBestPoints(0, motionData[motionData.length-1][0], 0.1, jointRotExtremas, weights)
        let optVelPoints = findBestPoints(0, derivatives[derivatives.length-1][0], 0.1, jointVelExtremas, weights)

        for(let i = 0; i < 5; i++) {
            optRotPoints = averageSmoothing(optRotPoints, 0.5)
            optVelPoints = averageSmoothing(optVelPoints, 0.5)
        }
        
        let optRotExtremas = getExtremas(optRotPoints, 1, 0, true)
        let optVelExtremas = getExtremas(optVelPoints, 1, 0, true)

        console.log("extremas")
        console.log(velExtremas)
        console.log(optRotExtremas)
        console.log(optVelExtremas)

        let points = []

        velExtremas.forEach(ext => {
            points.push(ext[0])
        })
        optRotExtremas.forEach(ext => {
            points.push(ext[0])
        })
        optVelExtremas.forEach(ext => {
            points.push(ext[0])
        })

        points = clusterPoints(points, 1)
        points.forEach(pt => {
            this.robotGroup.animationInterface.addBookmark(pt);
        })

        let scores = []

        let collisionGroups = getRobotPositions(this.robotGroup, points)

        for(let i = 0; i < collisionGroups.length - 1; i++) {
            for(let k = i + 1; k < collisionGroups.length; k++) {
                let score = measureCollision(collisionGroups[i][1], collisionGroups[k][1])
                scores.push([score, collisionGroups[i][0], collisionGroups[k][0], i, k])
            }
        }
        for(let i = 0; i < scores.length; i++) {
            let minIndex = i
            for(let k = i; k < scores.length; k++) {
                if(scores[k][0] < scores[minIndex][0]) {
                    minIndex = k
                }
            }
            let temp = scores[i]
            scores[i] = scores[minIndex]
            scores[minIndex] = temp
        }

        console.log("all cuts and scores")
        console.log(scores)

        let cuts = createSeperations(collisionGroups, scores, this.numCuts, this.cutScoreThreshold)
        this.robotGroup.animationInterface.prerenderLine(null, cuts)
        return points
    }

    setCameraPosition(focusPoint, viewPoint) {
        this.interfaces.controls.object.position.copy(viewPoint);
        this.interfaces.controls.target.copy(focusPoint);    
        this.interfaces.controls.object.updateProjectionMatrix();
        this.interfaces.controls.dispatchEvent({ type: 'change' });
        this.interfaces.controls.update();
    }

    getOverallCamera(times, motionPlaneData, updateHeuristics=false) {
        let motionHeuristic = getMotionPlaneDirection(motionPlaneData)
        let poseHeuristic = getPoseCollisionDirection(this.robotGroup, times)
        let forwardHeuristic = getAverageForwardDirection(this.robotGroup, times)

        let heuristics = [
            [motionHeuristic[0], poseHeuristic[0], forwardHeuristic[0]], 
            [motionHeuristic[1], poseHeuristic[1], forwardHeuristic[1]]
        ]
        let weights = [
            [this.cameraWeights[0], this.cameraWeights[1], this.cameraWeights[2]], 
            [this.cameraWeights[3], this.cameraWeights[4], this.cameraWeights[5]]
        ]
        let cameraData = combineHeuristics(heuristics, weights)

        if(updateHeuristics) {
            this.cameraHeuristics = heuristics
        }
        
        let viewPoint = extendViewDistance(cameraData[0], cameraData[1], this.distanceFromFocus)
        this.setCameraPosition(viewPoint[0], viewPoint[1])
    }

    updateCamera() {
        if(this.cameraHeuristics.length > 0 && this.viewFromPoint) {
            let weights = [
                [this.cameraWeights[0], this.cameraWeights[1], this.cameraWeights[2]], 
                [this.cameraWeights[3], this.cameraWeights[4], this.cameraWeights[5]]
            ]
            let cameraData = combineHeuristics(this.cameraHeuristics, weights)
            let viewPoint = extendViewDistance(cameraData[0], cameraData[1], this.distanceFromFocus)
            this.setCameraPosition(viewPoint[0], viewPoint[1])
        }
    }
}